import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { ProspectDetails, WaChannel, WaState, WaTemplate } from "../../../services/v2/prospects.service";
import { dinero, errorTexto, nombreCompleto, SIMBOLO_PLANTEL } from "./salesHelpers";
import { PlantillaPicker, rellenar } from "./promoBloqueModal";

interface Props {
	campusId: number;
	det: ProspectDetails;
	asesora: string;
	onClose: () => void;
	/** Deja el mensaje escrito en el cuadro de WhatsApp de la ficha; la asesora lo revisa y lo manda. */
	onAlChat: (texto: string) => void;
	/** Ventana de 24 h de su WhatsApp: cerrada, la promo sale por plantilla aprobada. */
	wa: { state: WaState; channels: WaChannel[] } | null;
	onEnviado: () => void;
	/** "Ver lista": las demás que califican (vista Califican a promo de Matriculado). */
	onVerLista?: () => void;
}

/**
 * Promo que aplica (lienzo): el cálculo sobre lo que ya pagó en WAPP, las otras promos evaluadas, lo verificado,
 * y el mensaje listo. Las reglas viven en PromoEngine (WA_Promociones.md): sin descuento sobre descuento,
 * una sola promo, la que más le ahorra.
 */
const PromoModal: React.FC<Props> = ({ campusId, det, asesora, onClose, onAlChat, wa, onEnviado, onVerLista }) => {
	const http = HttpManager.getInstance();
	const w = det.wapp;
	const p = det.prospect;
	const promo = w?.promo || null;
	const best = promo?.best || null;
	const act = w?.active || null;
	const sim = SIMBOLO_PLANTEL[act?.campus_id || p.campus_id || campusId] || "";
	const [texto, setTexto] = useState<string>(() => {
		if (!best || !act) return "";
		const saludo = new Date().getHours() < 12 ? "buenos días" : "buenas tardes";
		const nombre = (p.first_name || "").split(" ")[0];
		const sinPromo = dinero(promo!.listTotal, sim);
		const linea = best.covers && best.covers >= promo!.remaining
			? `con pagar ${dinero(best.payNow, sim)} ya le quedan cubiertas todas las mensualidades que le faltan.`
			: `con pagar ${dinero(best.payNow, sim)} le quedan cubiertos ${best.covers} meses.`;
		return `Hola ${saludo} ${nombre}, cómo está? Le saluda ${asesora.split(" ")[0]} de Washington Academy :)\n\n`
			+ `Le escribo por la promo de su curso de ${act.curso.replace(/^Diplomado de /i, "")}.\n\n`
			+ `Sigue aplicando el ${best.name}: ${best.why}. En su caso, ${linea}\n\n`
			+ (promo!.listTotal > (best.payNow || 0) ? `Si no la toma terminaría pagando ${sinPromo}. Es buen ahorro!\n\n` : "")
			+ `Le comparto más información?`;
	});

	// Ventana cerrada (más de 24 h sin que escriba): plantilla con {{1}} nombre, {{2}} curso, {{3}} a pagar, {{4}} sin promo, {{5}} hasta cuándo.
	const cerrada = !!wa && !wa.state.windowOpen;
	const [channelId, setChannelId] = useState<number | null>(wa?.state.channelId || wa?.channels[0]?.id || null);
	const [tpl, setTpl] = useState<WaTemplate | null>(null);
	const [params, setParams] = useState<string[]>(() => {
		if (!best || !act) return ["", "", "", "", ""];
		const dom = new Date(); dom.setDate(dom.getDate() + ((7 - dom.getDay()) % 7 || 7));
		const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
		return [(p.first_name || "").split(" ")[0] || "qué tal", act.curso, dinero(best.payNow, sim), dinero(promo!.listTotal, sim), `el domingo ${dom.getDate()} de ${meses[dom.getMonth()]}`];
	});
	const [confirmar, setConfirmar] = useState<boolean>(false);
	const [enviando, setEnviando] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const mandarPlantilla = async () => {
		if (!tpl) return;
		setEnviando(true); setError(null);
		try {
			await http.prospectsService.sendWhatsapp(campusId, p.id, { template: tpl.name, language: tpl.language, params: params.slice(0, tpl.params), channelId });
			onEnviado(); onClose();
		} catch (e) { setError(errorTexto(e)); setConfirmar(false); }
		finally { setEnviando(false); }
	};

	const verificado = useMemo(() => {
		if (!act) return [];
		return [
			{ ok: act.status_id === 1, t: act.status_id === 1 ? "Activa en clase hoy" : "Su matrícula no está activa (" + act.estatus + ")" },
			{ ok: (act.pagadas || 0) < 3, t: (act.pagadas || 0) < 3 ? "Todavía no paga la 3ª mensualidad" : "Ya pagó la 3ª mensualidad: multi-mes solo si es 2º curso" },
			{ ok: !(p.last_promo_message_at && Date.now() - new Date(p.last_promo_message_at.replace(" ", "T") + "Z").getTime() < 7 * 86400000), t: "No recibió promoción en los últimos 7 días" },
			{ ok: true, t: "Saldo leído de sus pagos en WAPP" },
			{ ok: true, t: best ? "No hay otra promo que le convenga más" : "Ninguna promo le aplica hoy" },
		];
	}, [act, best, p.last_promo_message_at]);

	return (
		<Modal show onHide={onClose} size="xl" className="ventas-nuevo-modal">
			<Modal.Header closeButton>
				<Modal.Title style={{ fontFamily: "Bricolage, sans-serif", fontWeight: 800 }}>Promo que aplica</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="apagado" style={{ marginBottom: 14 }}>{nombreCompleto(p)}{act ? " · " + act.curso + " · " + act.modulo : ""}</div>
				{!w || !act ? <div className="aviso">No es alumno con matrícula activa en WAPP: las promos de mensualidad se calculan sobre su matrícula.</div> : (
					<div className="pm-cuerpo">
						<div className="pm-izq">
							{best ? (
								<div className="pm-mejor">
									<div className="pm-titulo"><span className="pm-tag">{best.name}</span><span>{best.why}</span></div>
									<div className="par"><span>Curso de</span><span>{act.meses} meses</span></div>
									<div className="par"><span>Mensualidades pagadas</span><span>{act.pagadas || 0}</span></div>
									<div className="par"><span>Le faltan</span><span>{promo!.remaining}</span></div>
									<div className="par"><span>Mensualidad</span><span>{dinero(act.monthly_billing_price, sim)}</span></div>
									<hr />
									<div className="par grande"><span>Paga ahora{best.covers ? ` (${best.covers} meses)` : ""}</span><span className="acento">{dinero(best.payNow, sim)}</span></div>
									<div className="par"><span>Sin la promo pagaría</span><span className="tachado">{dinero(promo!.listTotal, sim)}</span></div>
									<div className="par grande"><span>Se ahorra</span><span className="verde">{dinero(best.saves, sim)}</span></div>
								</div>
							) : <div className="aviso">{promo?.reason || "Ninguna promo le aplica con lo que ha pagado."}</div>}
							<div className="tarjeta">
								<h4>Verificado antes de ofrecer</h4>
								{verificado.map((v, i) => <div key={i} className="pm-check"><span className={v.ok ? "ok" : "no"}>{v.ok ? "✓" : "!"}</span><span>{v.t}</span></div>)}
							</div>
							<div className="tarjeta">
								<h4>Otras evaluadas</h4>
								{(promo?.evaluated || []).filter((e) => !best || e.code !== best.code).map((e) => (
									<div key={e.code} className="par"><span>{e.name}</span><span className="apagado">{e.applies ? "ahorra " + dinero(e.saves, sim) + " · menor" : "no aplica · " + e.why}</span></div>
								))}
							</div>
						</div>
						<div className="pm-der">
							{cerrada ? (
								<>
									<h4>Plantilla de promo</h4>
									<div className="aviso">Pasaron más de 24 h desde su último mensaje: WhatsApp solo deja mandar una plantilla aprobada.</div>
									<PlantillaPicker campusId={campusId} channels={wa!.channels} channelId={channelId} onChannel={setChannelId} template={tpl} onTemplate={setTpl} />
									{tpl ? (
										<>
											{Array.from({ length: tpl.params }).map((_, i) => (
												<label key={i} className="pb-param">{"{{" + (i + 1) + "}}"} {["nombre", "curso", "a pagar con la promo", "sin la promo", "hasta cuándo"][i] || ""}
													<input type="text" value={params[i] || ""} onChange={(e) => { const n = [...params]; n[i] = e.target.value; setParams(n); }} />
												</label>
											))}
											<h4>Así le llega</h4>
											<div className="pb-vista">{rellenar(tpl.body, params)}</div>
										</>
									) : null}
									{error ? <div className="aviso error">{error}</div> : null}
								</>
							) : (
								<>
									<h4>Mensaje</h4>
									<textarea rows={12} value={texto} onChange={(e) => setTexto(e.target.value)} aria-label="Mensaje de la promo" />
									<div className="apagado">Queda registrada en la bitácora al mandarse.</div>
								</>
							)}
						</div>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				{onVerLista ? <button className="enlace" style={{ marginRight: "auto" }} onClick={() => { onVerLista(); onClose(); }}>Ver lista de las que califican</button> : null}
				{confirmar ? (
					<>
						<span className="oro">Se manda la plantilla «{tpl?.name}» por WhatsApp.</span>
						<button className="btn-wa" disabled={enviando} onClick={() => setConfirmar(false)}>No</button>
						<button className="btn-wa pri" disabled={enviando} onClick={mandarPlantilla}>{enviando ? "Enviando…" : "Sí, mandar"}</button>
					</>
				) : (
					<>
						<button className="btn-wa" onClick={onClose}>Cancelar</button>
						{cerrada ? <button className="btn-wa pri" disabled={!tpl || !best} onClick={() => setConfirmar(true)}>Mandar plantilla</button>
							: <button className="btn-wa pri" disabled={!texto.trim()} onClick={() => { onAlChat(texto); onClose(); }}>Poner en el chat</button>}
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default PromoModal;
